import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@/lib/auth";
import { getFirebase } from "@/lib/firebase";
import { canonicalOrigin } from "@/lib/origin";
import { routeTree } from "./routeTree.gen";
import "./styles/globals.css";

// One address per site. The *.web.app / *.firebaseapp.com aliases are not
// Auth authorized domains, so action links minted from them fail; sending
// visitors to the custom domain before anything initialises keeps every
// bookmark, email link and support instruction on admin[-staging].pinstaq.com.
const canonical = canonicalOrigin(window.location);
const onCanonicalHost = canonical === window.location.origin;
if (!onCanonicalHost) {
  const { pathname, search, hash } = window.location;
  window.location.replace(new URL(pathname + search + hash, canonical).href);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient, auth: undefined! },
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Nothing below runs on a host we are leaving: Firebase + App Check init and
// the first render would be discarded work on a page that is navigating away.
if (onCanonicalHost) {
  // Initialise Firebase + App Check eagerly so route loaders + AuthProvider can use them.
  getFirebase();

  const rootEl = document.getElementById("root");
  if (!rootEl) throw new Error("#root element not found");

  createRoot(rootEl).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}
